import { useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { tr } from "./i18n.js";

const ADMINISTRARE = ["ADMIN_TENANT", "MANAGER_TEHNIC"];

const CATEGORII = [
  { cod: "", titlu: "Toate categoriile" },
  { cod: "PIF", titlu: "Punere în funcțiune" },
  { cod: "Reparatie", titlu: "Reparație" },
  { cod: "Mentenanta", titlu: "Mentenanță" },
  { cod: "Extindere", titlu: "Extindere" },
  { cod: "Scoatere", titlu: "Scoatere" },
];

const NIVEL = ["potrivit exact", "subsistem", "sistem"];

function durata(minute) {
  if (minute < 60) return `${minute} min`;
  const ore = Math.floor(minute / 60);
  const rest = minute % 60;
  return rest ? `${ore} h ${rest} min` : `${ore} h`;
}

export default function Operatii({ eu }) {
  const [arbore, setArbore] = useState([]);
  const [sistem, setSistem] = useState("");
  const [tipComponenta, setTipComponenta] = useState("");
  const [categorie, setCategorie] = useState("");
  const [filtru, setFiltru] = useState("");
  const [lista, setLista] = useState([]);
  const [stat, setStat] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [editat, setEditat] = useState(null);

  const poateAdministra = ADMINISTRARE.includes(eu.rol);

  useEffect(() => {
    api.arbore().then(setArbore).catch((e) => setEroare(mesajEroare(e)));
  }, []);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      setLista(await api.operatii({
        sistem: tipComponenta ? undefined : sistem || undefined,
        tipComponenta: tipComponenta || undefined,
        categorie: categorie || undefined,
      }));
      if (poateAdministra) setStat(await api.statisticiOperatii());
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, [sistem, tipComponenta, categorie, poateAdministra]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const sistemAles = useMemo(() => arbore.find((s) => s.id === sistem), [arbore, sistem]);

  const vizibile = useMemo(() => {
    const q = filtru.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter((o) => (o.denumire + " " + o.cod).toLowerCase().includes(q));
  }, [lista, filtru]);

  async function actiune(promisiune) {
    setEroare("");
    try { await promisiune; await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  return (
    <div className="continut">
      <h2>{tr("Operații tehnice")}</h2>
      <p className="subtitlu">
        {tr("Ce se poate face pe fiecare echipament. Pe teren tehnicianul le alege din listă, nu le scrie — de asta depinde raportarea pe elemente și norma de timp.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      {stat && stat.deCalibrat > 0 && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>{stat.deCalibrat} {tr("din")} {stat.total} {tr("operații au normă de calibrat.")}</strong>{" "}
            {tr("Normele livrate sunt estimări plauzibile pentru un tehnician cu experiență, nu măsurători din firma voastră — iar din ele ies planificarea și ofertele. Corectează-le pe cele pe care le faceți des; la prima corectură, marcajul dispare și nu se mai schimbă niciodată de la sine.")}
          </div>
        </div>
      )}

      <div className="panou">
        <div className="panou-corp">
          <div className="formular-linie">
            <label>
              {tr("Sistem")}
              <select className="camp" value={sistem} onChange={(e) => { setSistem(e.target.value); setTipComponenta(""); }}>
                <option value="">{tr("Toate sistemele")}</option>
                {arbore.map((s) => <option key={s.id} value={s.id}>{tr(s.denumire)}</option>)}
              </select>
            </label>
            <label>
              {tr("Echipament (ca pe teren)")}
              <select className="camp" value={tipComponenta} onChange={(e) => setTipComponenta(e.target.value)} disabled={!sistemAles}>
                <option value="">{tr("— fără context —")}</option>
                {sistemAles?.subsisteme.flatMap((ss) =>
                  ss.tipuriComponenta.map((t) => (
                    <option key={t.id} value={t.id}>{tr(ss.denumire)} · {tr(t.denumire)}</option>
                  )))}
              </select>
            </label>
            <label>
              {tr("Categorie de intervenție")}
              <select className="camp" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
                {CATEGORII.map((c) => <option key={c.cod} value={c.cod}>{tr(c.titlu)}</option>)}
              </select>
            </label>
          </div>
          <p className="nota-cautare">
            {tr("Alege un echipament și o categorie ca să vezi exact lista pe care o primește tehnicianul pe teren, în ordinea în care o primește.")}
          </p>
        </div>
      </div>

      <input
        className="camp camp-cautare"
        placeholder={tr("Filtrează operațiile…")}
        value={filtru}
        onChange={(e) => setFiltru(e.target.value)}
      />

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : vizibile.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">{tr("Nicio operație pentru filtrele alese.")}</div></div>
      ) : (
        <div className="panou">
          <div className="panou-antet">
            <span>{vizibile.length} {tr("operații")}</span>
            {tipComponenta && <span className="numar">{tr("în ordinea potrivirii cu echipamentul ales")}</span>}
          </div>
          <div className="panou-corp">
            <table className="tabel tabel-operatii">
              <thead>
                <tr>
                  <th>{tr("Operație")}</th>
                  <th>{tr("Se aplică la")}</th>
                  <th>{tr("Categorii")}</th>
                  <th className="num">{tr("Normă")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vizibile.map((o) => (
                  <tr key={o.id} className={o.activ === false ? "tip-dezactivat" : ""}>
                    <td>
                      {tr(o.denumire)}
                      <code className="cod-mic">{o.cod}</code>
                      {o.necesitaFotoInainte && <span className="marcaj" title={tr("Fotografie obligatorie înainte")}>{tr("foto înainte")}</span>}
                      {o.necesitaFotoDupa && <span className="marcaj" title={tr("Fotografie obligatorie după")}>{tr("foto după")}</span>}
                      {o.necesitaMasuratoare && <span className="marcaj" title={tr("Valoare măsurată obligatorie")}>{tr("măsurătoare")}</span>}
                    </td>
                    <td>
                      {tr(o.tipComponenta?.denumire || o.subsistem?.denumire || o.tipSistem?.denumire)}
                      {o.potrivire !== undefined && (
                        <span className={"eticheta " + (o.potrivire === 0 ? "eticheta-activ" : "")}>
                          {tr(NIVEL[o.potrivire])}
                        </span>
                      )}
                    </td>
                    <td className="celula-categorii">
                      {o.categoriiInterventie.map((c) => (
                        <span key={c} className="marcaj">{c === "Mentenanta" ? tr("Mentenanță") : c === "Reparatie" ? tr("Reparație") : c}</span>
                      ))}
                    </td>
                    <td className="num">
                      {durata(o.normaTimpMinute)}
                      {o.normaDeCalibrat && <span className="fara-pret" title={tr("Estimare livrată de noi, necalibrată")}>{tr("de calibrat")}</span>}
                    </td>
                    <td className="celula-actiuni">
                      {poateAdministra && (
                        o.activ === false ? (
                          <button className="buton buton-mic" onClick={() => actiune(api.reactiveazaOperatie(o.id))}>
                            {tr("reactivează")}
                          </button>
                        ) : (
                          <button className="buton buton-mic buton-discret" onClick={() => setEditat(o)}>
                            {tr("schimbă norma")}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editat && (
        <FormularNorma
          operatie={editat}
          laInchidere={() => setEditat(null)}
          laSalvare={async (date) => { await actiune(api.schimbaNorma(editat.id, date)); setEditat(null); }}
        />
      )}
    </div>
  );
}

function FormularNorma({ operatie, laInchidere, laSalvare }) {
  const [minute, setMinute] = useState(String(operatie.normaTimpMinute));
  const [motiv, setMotiv] = useState("");
  const valid = Number(minute) >= 1 && Number(minute) <= 600;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr(operatie.denumire)}</h3>
        <p className="subtitlu">
          {operatie.normaDeCalibrat
            ? tr("Norma de acum e o estimare livrată de noi. Odată ce o corectezi, devine a voastră și nu se mai schimbă de la sine.")
            : tr("Norma a fost deja calibrată de voi. O poți ajusta oricând.")}
        </p>
        <div className="formular-linie">
          <label>
            {tr("Normă de timp (minute)")}
            <input className="camp" type="number" min="1" max="600" value={minute}
              onChange={(e) => setMinute(e.target.value)} autoFocus />
          </label>
          <label>
            {tr("Motivul (opțional)")}
            <input className="camp" value={motiv} onChange={(e) => setMotiv(e.target.value)}
              placeholder={tr("Măsurat pe 12 intervenții în august")} />
          </label>
        </div>
        <p className="nota-cautare">
          {tr("Norma nu se folosește la facturare — manopera se calculează din timpul real din fișă. Contează la planificare, la ofertele estimative și la analiza estimat vs. realizat.")}
        </p>
        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton" disabled={!valid}
            onClick={() => laSalvare({ normaTimpMinute: Number(minute), motiv: motiv.trim() || undefined })}>
            {tr("salvează")}
          </button>
        </div>
      </div>
    </div>
  );
}
